// Percent helpers for the scholarship editor. The engine stores INTEGER BASIS
// POINTS (1000 = 10.00%); Nipa types a percent. Same discipline as money.ts:
// we do NOT use `parseFloat(x) * 100` (it mis-rounds); parsing is done from the
// string with integer maths.
//
// Unlike audToCents, nothing but outer whitespace is stripped: "%" is rejected
// (the input renders its own % adornment) and comma decimals ("10,5") are
// rejected rather than stripped — silently reading "0,5" as 5% would be a
// wrong-by-10× bug.

/**
 * Parse a percent string to integer basis points, WITHOUT floating-point
 * error. Valid domain 0.01–100 (1–10000 bp), max two decimals. Returns `null`
 * for anything else — empty, zero, over 100, signs, 3+ decimals, comma
 * decimals, exponents, symbols — so the caller can block the save. Zero is
 * never a scholarship value: removal is kind 'none', not 0 bp.
 *
 * "10" → 1000 · "10.5" → 1050 · "12.25" → 1225 · "0.01" → 1 · "100" → 10000
 */
export function percentToBp(input: string): number | null {
  const cleaned = input.trim();
  if (cleaned === '') return null;
  // Digits with an optional 1–2 decimal places (or a bare ".dd"). No sign is
  // allowed, so the result can never be negative.
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  // padEnd, not padStart: ".5" means 5 tenths of a percent = 50 bp.
  const bp = Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(bp)) return null;
  return bp >= 1 && bp <= 10000 ? bp : null;
}

/**
 * Integer basis points → a plain editable percent string (no "%" suffix) with
 * trailing zeros trimmed, for pre-filling the percent input. Integer maths
 * only. 1000 → "10" · 1050 → "10.5" · 1225 → "12.25" · 1 → "0.01".
 * Out-of-domain input (not an integer 1–10000) → "" (an empty input).
 */
export function bpToInput(bp: number): string {
  if (!Number.isInteger(bp) || bp < 1 || bp > 10000) return '';
  const whole = Math.trunc(bp / 100);
  const frac = bp % 100;
  if (frac === 0) return `${whole}`;
  // A two-digit frac has at most one trailing zero (frac ≠ 0), so one strip
  // suffices: 50 → "5", 25 → "25", 01 stays "01".
  return `${whole}.${String(frac).padStart(2, '0').replace(/0$/, '')}`;
}

/**
 * Integer basis points → a display percent string, trailing zeros trimmed.
 * 1000 → "10%" · 1050 → "10.5%" · 1225 → "12.25%" · 1 → "0.01%".
 * Out-of-domain → "—" (mirrors centsToAudDisplay's null → "—"; the API pins bp
 * to an integer 1–10000, so this is a render-honestly fallback, never a fix-up).
 */
export function bpToPercentDisplay(bp: number): string {
  const input = bpToInput(bp);
  return input === '' ? '—' : `${input}%`;
}
